
// frontend/src/pages/RelatoriosPage/RelatoriosPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Typography, Card, Form, Select, DatePicker, Button, Table, Tag, Statistic, Row, Col, Space, message, Input, Tooltip, Tabs, Grid, Progress, Collapse, Alert } from 'antd';
import { SearchOutlined, FilePdfOutlined, FileExcelOutlined, FilterOutlined, InfoCircleOutlined, BankOutlined, CreditCardOutlined, TrophyOutlined, RiseOutlined, CalculatorOutlined, WarningOutlined, UnorderedListOutlined, TeamOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getTransacoes, getResumoParcelamentos } from '../../services/transactionService';
import { getContas } from '../../services/accountService';
import { getCategorias } from '../../services/categoryService';
import { getCartoes } from '../../services/cardService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import logoImg from '../../assets/images/logo_horizontal.jpg'; // Importando a logo
import './RelatoriosPage.css';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import RelatoriosPortadorTab from './RelatoriosPortadorTab';
import ParcelamentosFuturosChart from '../../components/CartoesAnalytics/ParcelamentosFuturosChart';
import UsoPorDiaChart from '../../components/CartoesAnalytics/UsoPorDiaChart';
import { useNavigate, useSearchParams } from 'react-router-dom';

const { Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option, OptGroup } = Select;

const ABAS_VALIDAS = ['transacoes', 'parcelamentos', 'cartoes', 'portadores'];

// Mesmos regimes do Meu Desempenho; 'lancamento' = data de cada transação (comportamento original)
const REGIME_LANCAMENTO = 'lancamento';
const REGIMES_RELATORIO = ['competencia', 'fatura'];

const idsDoParam = (valor) =>
    (valor || '').split(',').map((x) => parseInt(x, 10)).filter((n) => Number.isInteger(n) && n > 0);

/**
 * Filtros iniciais vindos do link (ex.: clique num gráfico do Meu Desempenho):
 * ?inicio=AAAA-MM-DD&fim=AAAA-MM-DD&categorias=1,2&cartoes=3&contas=4&tipo=despesa&regime=fatura
 */
function filtrosIniciaisDaUrl(searchParams) {
    const inicio = dayjs(searchParams.get('inicio'), 'YYYY-MM-DD', true);
    const fim = dayjs(searchParams.get('fim'), 'YYYY-MM-DD', true);
    const tipo = searchParams.get('tipo');
    const regime = searchParams.get('regime');
    return {
        periodo: inicio.isValid() && fim.isValid() && !fim.isBefore(inicio)
            ? [inicio, fim]
            : [dayjs().startOf('month'), dayjs().endOf('month')],
        categorias: idsDoParam(searchParams.get('categorias')),
        cartoes: idsDoParam(searchParams.get('cartoes')),
        contas: idsDoParam(searchParams.get('contas')),
        tipo: ['receita', 'despesa', 'transferencia'].includes(tipo) ? tipo : null,
        status: null,
        regime: REGIMES_RELATORIO.includes(regime) ? regime : REGIME_LANCAMENTO,
    };
}

const RelatoriosPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [form] = Form.useForm();
    // Lido uma vez: depois o formulário manda, não o link
    const [filtrosIniciais] = useState(() => filtrosIniciaisDaUrl(searchParams));
    const [vindoDoDesempenho, setVindoDoDesempenho] = useState(
        () => searchParams.get('origem') === 'desempenho',
    );
    // Aplica no backend os portadores/cartões ocultos no Meu Desempenho
    const [usarFiltrosDesempenho, setUsarFiltrosDesempenho] = useState(
        () => searchParams.get('filtros_desempenho') === '1',
    );
    const [loading, setLoading] = useState(false);
    const [transacoes, setTransacoes] = useState([]);
    const [contas, setContas] = useState([]);
    const [cartoes, setCartoes] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [pageSize, setPageSize] = useState(20);
    /** Contas consideradas no último filtro (para saldo inicial/atual). null = todas. */
    const [reportContasIds, setReportContasIds] = useState(null);

    // Aba e dados de Parcelamentos
    const abaInicial = ABAS_VALIDAS.includes(searchParams.get('aba'))
        ? searchParams.get('aba')
        : 'transacoes';
    const [activeTabKey, setActiveTabKey] = useState(abaInicial);
    const [parcelamentos, setParcelamentos] = useState([]);
    const [loadingParcelamentos, setLoadingParcelamentos] = useState(false);
    const [filtroParcelamentosTipo, setFiltroParcelamentosTipo] = useState(null);
    const [filtroParcelamentosOrigem, setFiltroParcelamentosOrigem] = useState(null);
    const [filtroParcelamentosDescricao, setFiltroParcelamentosDescricao] = useState('');
    const [filtroParcelamentosSituacao, setFiltroParcelamentosSituacao] = useState(null);
    const [filtroParcelamentosCartao, setFiltroParcelamentosCartao] = useState(null);
    const [pageSizeParcelamentos, setPageSizeParcelamentos] = useState(20);

    // Estados para controle de exclusividade dos filtros
    const [disableContas, setDisableContas] = useState(() => filtrosIniciais.cartoes.length > 0);
    const [disableCartoes, setDisableCartoes] = useState(() => filtrosIniciais.contas.length > 0);

    const screens = Grid.useBreakpoint();
    const isMobile = screens.sm === false;

    // Carregar Contas, Cartões e Categorias no Mount
    useEffect(() => {
        const fetchAuxData = async () => {
            try {
                const [contasData, cartoesData, categoriasData] = await Promise.all([
                    getContas(),
                    getCartoes(),
                    getCategorias()
                ]);
                setContas(contasData.results || contasData || []);
                setCartoes(cartoesData.results || cartoesData || []);
                setCategorias(categoriasData || []);
            } catch (error) {
                console.error("Erro ao carregar dados auxiliares", error);
                message.error("Erro ao carregar filtros.");
            }
        };
        fetchAuxData();
    }, []);

    // Lógica de Exclusividade de Filtros
    const handleValuesChange = (changedValues, allValues) => {
        if (changedValues.contas) {
            const hasContas = changedValues.contas.length > 0;
            setDisableCartoes(hasContas);
            if (hasContas) {
                form.setFieldsValue({ cartoes: [] }); // Limpa cartões se selecionar conta
            }
        }
        if (changedValues.cartoes) {
            const hasCartoes = changedValues.cartoes.length > 0;
            setDisableContas(hasCartoes);
            if (hasCartoes) {
                form.setFieldsValue({ contas: [] }); // Limpa contas se selecionar cartão
            }
        }
    };

    const handleSearch = React.useCallback(async () => {
        setLoading(true);
        try {
            // Garante que o form tenha valores iniciais se ainda não foi tocado
            const values = form.getFieldsValue();
            const range = values.periodo || [dayjs().startOf('month'), dayjs().endOf('month')];

            const params = {
                start_date: range[0].format('YYYY-MM-DD'),
                end_date: range[1].format('YYYY-MM-DD'),
                contas: values.contas?.join(','),
                cartoes: values.cartoes?.join(','),
                categorias: values.categorias?.join(','),
                tipo: values.tipo,
                status: values.status,
                search: values.search,
                regime: REGIMES_RELATORIO.includes(values.regime) ? values.regime : undefined,
                filtros_desempenho: usarFiltrosDesempenho ? '1' : undefined,
                all: 'true'
            };

            const data = await getTransacoes(params);
            const lista = Array.isArray(data) ? data : (data.results || []);
            setTransacoes(lista);
            setReportContasIds(values.contas?.length ? values.contas : null);

        } catch (error) {
            console.error("Erro na busca", error);
            message.error("Falha ao buscar relatórios.");
        } finally {
            setLoading(false);
        }
    }, [form, usarFiltrosDesempenho]);

    // Busca inicial
    useEffect(() => {
        handleSearch();
    }, [handleSearch]);

    const handleTabChange = (key) => {
        setActiveTabKey(key);
        const next = new URLSearchParams(searchParams);
        if (key === 'transacoes') next.delete('aba');
        else next.set('aba', key);
        setSearchParams(next, { replace: true });
    };

    // A lista de parcelamentos era buscada dentro de handleTabChange, ou seja, só no
    // clique da aba. Como a página aceita ?aba=parcelamentos, abrir o link direto (ou
    // recarregar já nessa aba) deixava a tabela vazia sem erro nenhum. No efeito, os
    // dois caminhos ficam cobertos.
    useEffect(() => {
        if (activeTabKey !== 'parcelamentos') return undefined;
        let ativo = true;
        setLoadingParcelamentos(true);
        getResumoParcelamentos()
            .then((data) => { if (ativo) setParcelamentos(Array.isArray(data) ? data : []); })
            .catch(() => {
                if (!ativo) return;
                message.error('Falha ao carregar parcelamentos.');
                setParcelamentos([]);
            })
            .finally(() => { if (ativo) setLoadingParcelamentos(false); });
        return () => { ativo = false; };
    }, [activeTabKey]);

    const totais = useMemo(() => {
        const semTransferencias = transacoes.filter(item => !item.is_transferencia);
        return semTransferencias.reduce((acc, item) => {
            const valor = parseFloat(item.valor || 0);
            if (item.tipo === 'receita') {
                acc.receitas += valor;
                acc.saldo += valor;
            } else if (item.tipo === 'despesa') {
                acc.despesas += valor;
                acc.saldo -= valor;
            }
            return acc;
        }, { receitas: 0, despesas: 0, saldo: 0 });
    }, [transacoes]);

    /** Contas no escopo do relatório (filtro aplicado). */
    const contasInScope = useMemo(() => {
        return reportContasIds?.length
            ? contas.filter(c => reportContasIds.includes(c.id))
            : contas;
    }, [contas, reportContasIds]);

    /** Saldo atual (já contabiliza saldo inicial + receitas - despesas) das contas do relatório. */
    const saldosContas = useMemo(() => {
        const saldoAtual = contasInScope.reduce((s, c) => s + parseFloat(c.saldo_atual || 0), 0);
        return { saldoAtual, contasCount: contasInScope.length };
    }, [contasInScope]);

    /** Linhas de "Saldo inicial" para a tabela (uma por conta, com data de criação da conta). */
    const linhasSaldoInicial = useMemo(() => {
        return contasInScope.map(c => ({
            id: `saldo-inicial-${c.id}`,
            descricao: 'Saldo inicial',
            data_vencimento: c.criado_em ? dayjs(c.criado_em).format('YYYY-MM-DD') : null,
            conta_nome: c.nome,
            valor: String(c.saldo_inicial ?? 0),
            categoria_nome: null,
            tipo: 'saldo_inicial',
            isSaldoInicial: true,
            status: '-',
            conta_id: c.id,
        }));
    }, [contasInScope]);

    /** Tabela = linhas de saldo inicial + transações, ordenadas por data. */
    const dadosTabela = useMemo(() => {
        const combined = [...linhasSaldoInicial, ...transacoes];
        return combined.sort((a, b) => {
            const da = a.data_vencimento || '';
            const db = b.data_vencimento || '';
            return da.localeCompare(db);
        });
    }, [linhasSaldoInicial, transacoes]);

    // Cálculo de Insights
    const insights = useMemo(() => {
        const despesas = transacoes.filter(t => t.tipo === 'despesa' && !t.is_transferencia);
        
        // Maior despesa
        const maiorDespesa = despesas.length > 0 
            ? despesas.reduce((max, t) => parseFloat(t.valor) > parseFloat(max.valor) ? t : max)
            : null;

        // Categoria com mais gastos
        const gastosPorCategoria = despesas.reduce((acc, t) => {
            const categoria = t.categoria_nome || 'Sem Categoria';
            const valor = parseFloat(t.valor || 0);
            acc[categoria] = (acc[categoria] || 0) + valor;
            return acc;
        }, {});

        const categoriaTopGasto = Object.keys(gastosPorCategoria).length > 0
            ? Object.entries(gastosPorCategoria).reduce((max, [cat, val]) => 
                val > max.valor ? { nome: cat, valor: val } : max
              , { nome: '', valor: 0 })
            : null;

        // Média de gastos por dia
        const periodoValues = form.getFieldValue('periodo');
        let mediaPorDia = 0;
        if (periodoValues && periodoValues[0] && periodoValues[1]) {
            const dias = periodoValues[1].diff(periodoValues[0], 'days') + 1;
            mediaPorDia = dias > 0 ? totais.despesas / dias : 0;
        }

        // Transações acima de R$ 500
        const transacoesGrandes = despesas.filter(t => parseFloat(t.valor) >= 500);

        return {
            maiorDespesa,
            categoriaTopGasto,
            mediaPorDia,
            transacoesGrandes: transacoesGrandes.length,
            totalTransacoesGrandes: transacoesGrandes.reduce((sum, t) => sum + parseFloat(t.valor), 0)
        };
    }, [transacoes, totais.despesas, form]);

    const exportPDF = () => {
        const doc = new jsPDF();

        const img = new Image();
        img.src = logoImg;

        img.onload = () => {
            // 1. Adicionar Logo com Aspect Ratio Correto
            const logoWidth = 50;
            const ratio = img.height / img.width;
            const logoHeight = logoWidth * ratio;

            try {
                doc.addImage(img, 'JPEG', 14, 10, logoWidth, logoHeight);
            } catch (err) {
                // Fallback se imagem falhar
                doc.setFontSize(22);
                doc.setTextColor(0, 150, 136);
                doc.text("SALDO ATIVO", 14, 20);
            }

            continuePDFGeneration(doc, logoHeight);
        };

        img.onerror = () => {
            // Fallback se imagem não carregar
            doc.setFontSize(22);
            doc.setTextColor(0, 150, 136);
            doc.text("SALDO ATIVO", 14, 20);
            continuePDFGeneration(doc, 15);
        }
    };

    const continuePDFGeneration = (doc, logoHeight) => {
        // Cabeçalho: logo à esquerda e bloco de informações à direita, alinhados verticalmente
        const pageWidth = doc.internal.pageSize.width;
        const marginRight = 14;
        const logoTop = 10;
        const logoCenterY = logoTop + logoHeight / 2;

        // Bloco direito: centralizado verticalmente com a logo
        // Em jsPDF, Y em text() é a baseline. Ajustamos para o centro do bloco coincidir com o centro da logo.
        const lineHeight = 7;
        const firstLineBaseline = logoCenterY - lineHeight; // ~centro do bloco de 3 linhas

        const textEndX = pageWidth - marginRight;

        doc.setFontSize(16);
        doc.setTextColor(40);
        doc.text("Relatório Financeiro", textEndX, firstLineBaseline, { align: 'right' });

        doc.setFontSize(10);
        doc.setTextColor(100);

        let periodText = 'Período Personalizado';
        const periodoValue = form.getFieldValue('periodo');
        if (periodoValue && periodoValue[0] && periodoValue[1]) {
            periodText = `${periodoValue[0].format('DD/MM/YYYY')} a ${periodoValue[1].format('DD/MM/YYYY')}`;
        }

        doc.text(`Período: ${periodText}`, textEndX, firstLineBaseline + lineHeight, { align: 'right' });
        doc.text(`Gerado em: ${dayjs().format('DD/MM/YYYY HH:mm')}`, textEndX, firstLineBaseline + lineHeight * 2, { align: 'right' });

        // Define o startY para a tabela. 
        const headerBottom = Math.max(10 + logoHeight, 35);
        const tableStartY = headerBottom + 10;

        // Dados da Tabela (inclui linhas de saldo inicial + transações)
        const tableColumn = ["Data", "Descrição", "Categoria", "Origem", "Valor", "Status"];
        const tableRows = [];

        dadosTabela.forEach(t => {
            const dataStr = t.data_vencimento ? dayjs(t.data_vencimento).format('DD/MM/YYYY') : '-';
            const contaCartao = t.conta_nome || t.cartao_nome || '-';
            const statusStr = t.isSaldoInicial ? 'Saldo inicial (conta criada nesta data)' : t.status;
            const rowData = [
                dataStr,
                t.descricao,
                t.isSaldoInicial ? '-' : (t.categoria_nome || '-'),
                contaCartao,
                parseFloat(t.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
                statusStr
            ];
            tableRows.push(rowData);
        });

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: tableStartY,
            headStyles: { fillColor: [0, 150, 136] }, // Verde tema
            styles: { fontSize: 8 },
            alternateRowStyles: { fillColor: [245, 245, 245] },
        });

        // Totais
        // @ts-ignore
        let finalY = doc.lastAutoTable.finalY + 10;

        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text("RESUMO DO PERÍODO:", 14, finalY);

        doc.setTextColor(80, 80, 80);
        doc.text(`Saldo atual (contas): ${saldosContas.saldoAtual.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} (${saldosContas.contasCount} conta(s))`, 14, finalY + 7);

        doc.setTextColor(39, 174, 96); // Verde
        doc.text(`Total Receitas: ${totais.receitas.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, 14, finalY + 14);

        doc.setTextColor(192, 57, 43); // Vermelho
        doc.text(`Total Despesas: ${totais.despesas.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, 14, finalY + 21);

        const saldoColor = totais.saldo >= 0 ? [39, 174, 96] : [192, 57, 43];
        doc.setTextColor(saldoColor[0], saldoColor[1], saldoColor[2]);
        doc.setFont(undefined, 'bold');
        doc.text(`Resultado do período: ${totais.saldo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, 14, finalY + 28);

        doc.save(`saldo_ativo_${dayjs().format('YYYY-MM-DD')}.pdf`);
    };

    const exportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(dadosTabela.map(t => ({
            Data: t.data_vencimento ? dayjs(t.data_vencimento).format('DD/MM/YYYY') : '-',
            Descrição: t.descricao,
            Tipo: t.isSaldoInicial ? 'Saldo inicial' : t.tipo,
            Categoria: t.isSaldoInicial ? '-' : (t.categoria_nome || '-'),
            'Conta/Cartão': t.conta_nome || t.cartao_nome || '-',
            Valor: parseFloat(t.valor || 0),
            Status: t.isSaldoInicial ? 'Saldo inicial (conta criada nesta data)' : t.status
        })));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Transações");
        XLSX.writeFile(workbook, `saldo_ativo_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    };

    const columns = [
        {
            title: 'Data',
            dataIndex: 'data_vencimento',
            key: 'data',
            render: (text, record) => (record.isSaldoInicial ? (text ? dayjs(text).format('DD/MM/YYYY') : '-') : dayjs(text).format('DD/MM/YYYY')),
            sorter: (a, b) => (a.data_vencimento || '').localeCompare(b.data_vencimento || ''),
        },
        {
            title: 'Descrição',
            dataIndex: 'descricao',
            key: 'descricao',
        },
        {
            title: 'Categoria',
            dataIndex: 'categoria_nome',
            key: 'categoria',
            render: (text, record) => (record.isSaldoInicial ? '-' : (text || '-')),
        },
        {
            title: 'Conta / Cartão',
            key: 'conta',
            render: (text, record) => {
                if (record.conta_nome) {
                    return (
                        <Space>
                            <BankOutlined style={{ color: '#50B0A0' }} />
                            {record.conta_nome}
                        </Space>
                    );
                } else if (record.cartao_nome) {
                    return (
                        <Space>
                            <CreditCardOutlined style={{ color: '#2F9E6F' }} />
                            {record.cartao_nome}
                        </Space>
                    );
                }
                return '-';
            }
        },
        {
            title: 'Valor',
            dataIndex: 'valor',
            key: 'valor',
            align: 'right',
            render: (valor, record) => (
                <Text type={record.isSaldoInicial ? undefined : (record.tipo === 'despesa' ? 'danger' : 'success')}>
                    {parseFloat(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Text>
            ),
            sorter: (a, b) => parseFloat(a.valor || 0) - parseFloat(b.valor || 0),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => {
                if (record.isSaldoInicial) {
                    return <Tag color="default">Saldo inicial (conta criada nesta data)</Tag>;
                }
                return (
                    <Space wrap>
                        <Tag color={status === 'PAGO' ? 'green' : status === 'PENDENTE' ? 'orange' : 'default'}>
                            {status}
                        </Tag>
                        {record.is_transferencia && (
                            <Tag color="blue">Transferência</Tag>
                        )}
                    </Space>
                );
            }
        }
    ];

    // Agrupamento de categorias
    const categoriasReceita = categorias.filter(c => c.tipo === 'receita');
    const categoriasDespesa = categorias.filter(c => c.tipo === 'despesa');

    const fmtBRL = (v) => parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    // Filtros e totais da aba Parcelamentos
    const parcelamentosFiltrados = useMemo(() => {
        let list = [...(parcelamentos || [])];
        if (filtroParcelamentosDescricao?.trim()) {
            const termo = filtroParcelamentosDescricao.trim().toLowerCase();
            list = list.filter(p => (p.descricao || '').toLowerCase().includes(termo));
        }
        if (filtroParcelamentosTipo) list = list.filter(p => p.tipo === filtroParcelamentosTipo);
        if (filtroParcelamentosOrigem) list = list.filter(p => p.origem === filtroParcelamentosOrigem);
        if (filtroParcelamentosCartao) list = list.filter(p => p.cartao_id === filtroParcelamentosCartao);
        if (filtroParcelamentosSituacao === 'aberto') {
            list = list.filter(p => parseFloat(p.valor_restante || 0) > 0);
        } else if (filtroParcelamentosSituacao === 'quitado') {
            list = list.filter(p => parseFloat(p.valor_restante || 0) === 0);
        }
        list.sort((a, b) => {
            const ra = parseFloat(a.valor_restante || 0);
            const rb = parseFloat(b.valor_restante || 0);
            const abertoA = ra > 0 ? 0 : 1;
            const abertoB = rb > 0 ? 0 : 1;
            if (abertoA !== abertoB) return abertoA - abertoB;
            return rb - ra;
        });
        return list;
    }, [parcelamentos, filtroParcelamentosDescricao, filtroParcelamentosTipo, filtroParcelamentosOrigem, filtroParcelamentosCartao, filtroParcelamentosSituacao]);

    const totaisParcelamentos = useMemo(() => {
        const comprometido = parcelamentosFiltrados
            .filter(p => p.tipo === 'despesa')
            .reduce((s, p) => s + parseFloat(p.valor_restante || 0), 0);
        const quitado = parcelamentosFiltrados.reduce((s, p) => {
            const total = parseFloat(p.valor_total || 0);
            const restante = parseFloat(p.valor_restante || 0);
            return s + Math.max(0, total - restante);
        }, 0);
        const parcelasEmAberto = parcelamentosFiltrados.reduce((s, p) => {
            const restante = parseFloat(p.valor_restante || 0);
            if (restante <= 0) return s;
            const totalParc = parseInt(p.total_parcelas || 0, 10);
            const pagas = parseInt(p.parcelas_pagas || 0, 10);
            return s + Math.max(0, totalParc - pagas);
        }, 0);
        const receitasAReceber = parcelamentosFiltrados
            .filter(p => p.tipo === 'receita')
            .reduce((s, p) => s + parseFloat(p.valor_restante || 0), 0);
        const totalParcelas = parcelamentosFiltrados
            .filter(p => parseFloat(p.valor_restante || 0) > 0)
            .reduce((s, p) => s + parseFloat(p.valor_parcela || 0), 0);
        const totalGeral = parcelamentosFiltrados.reduce((s, p) => s + parseFloat(p.valor_restante || 0), 0);
        return { comprometido, quitado, parcelasEmAberto, receitasAReceber, totalParcelas, totalGeral };
    }, [parcelamentosFiltrados]);

    // Comparadores da ordenação da tabela de parcelamentos. `localeCompare` com
    // 'pt-BR' para acento ordenar junto da letra base ("Ártico" perto de "Ar", e não
    // depois de "Z"). Limpar a ordenação (3º clique) devolve a ordem padrão da lista:
    // em aberto primeiro, maior saldo restante no topo.
    const cmpNum = (extrair) => (a, b) => extrair(a) - extrair(b);
    const cmpTexto = (extrair) => (a, b) =>
        (extrair(a) || '').localeCompare(extrair(b) || '', 'pt-BR');
    const numCampo = (campo) => (r) => parseFloat(r[campo] || 0);
    const pctPago = (r) => {
        const total = parseInt(r.total_parcelas || 0, 10) || 1;
        return parseInt(r.parcelas_pagas || 0, 10) / total;
    };
    const valorPago = (r) =>
        Math.max(0, parseFloat(r.valor_total || 0) - parseFloat(r.valor_restante || 0));

    const columnsParcelamentos = [
        {
            title: 'Nome',
            dataIndex: 'descricao',
            key: 'descricao',
            ellipsis: true,
            width: 180,
            sorter: cmpTexto((r) => r.descricao),
        },
        {
            title: 'Conta / Cartão',
            key: 'conta_cartao',
            width: 120,
            ellipsis: true,
            render: (_, r) => r.cartao_nome || r.conta_nome || '—',
            sorter: cmpTexto((r) => r.cartao_nome || r.conta_nome || ''),
        },
        {
            title: 'Valor total',
            dataIndex: 'valor_total',
            key: 'valor_total',
            align: 'right',
            width: 105,
            render: (v) => fmtBRL(v),
            sorter: cmpNum(numCampo('valor_total')),
        },
        {
            title: 'Valor parc.',
            dataIndex: 'valor_parcela',
            key: 'valor_parcela',
            align: 'right',
            width: 100,
            render: (v) => fmtBRL(v),
            sorter: cmpNum(numCampo('valor_parcela')),
        },
        {
            title: 'Progresso',
            key: 'progresso',
            width: 140,
            sorter: cmpNum(pctPago),
            render: (_, r) => {
                const total = parseInt(r.total_parcelas || 0, 10) || 1;
                const pagas = parseInt(r.parcelas_pagas || 0, 10);
                const percent = Math.min(100, Math.round((pagas / total) * 100));
                return (
                    <div style={{ minWidth: 100 }}>
                        <Progress percent={percent} size="small" status={percent >= 100 ? 'success' : 'active'} />
                        <Text type="secondary" style={{ fontSize: 12 }}>{pagas}/{total}</Text>
                    </div>
                );
            },
        },
        {
            title: 'Valor pago',
            key: 'valor_pago',
            align: 'right',
            width: 105,
            sorter: cmpNum(valorPago),
            render: (_, r) => <Text type="success">{fmtBRL(valorPago(r))}</Text>,
        },
        {
            title: 'Valor restante',
            dataIndex: 'valor_restante',
            key: 'valor_restante',
            align: 'right',
            width: 115,
            sorter: cmpNum(numCampo('valor_restante')),
            render: (v, r) => {
                const n = parseFloat(v || 0);
                if (n === 0) return '—';
                return r.tipo === 'receita'
                    ? <Text type="success">{fmtBRL(n)}</Text>
                    : <Text type="danger">{fmtBRL(n)}</Text>;
            },
        },
        {
            title: 'Origem',
            key: 'origem',
            width: 90,
            sorter: cmpTexto((r) => (r.origem === 'compra_cartao' ? 'Cartão' : 'Conta')),
            render: (_, r) => r.origem === 'compra_cartao' ? <Tag color="green">Cartão</Tag> : <Tag color="blue">Conta</Tag>,
        },
        {
            title: 'Tipo',
            dataIndex: 'tipo',
            key: 'tipo',
            width: 90,
            sorter: cmpTexto((r) => r.tipo),
            render: (t) => t === 'receita' ? <Tag color="green">Receita</Tag> : <Tag color="red">Despesa</Tag>,
        },
    ];

    return (
        <Content style={{ padding: isMobile ? '12px' : '24px', maxWidth: 1200, margin: '0 auto', width: '100%' }}>
            <div
                className="relatorios-page-header"
                style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: isMobile ? 12 : 16,
                    marginBottom: 24,
                }}
            >
                <Title
                    level={isMobile ? 3 : 2}
                    style={{
                        margin: 0,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isMobile ? 'nowrap' : 'normal',
                    }}
                >
                    {isMobile ? 'Relatórios' : 'Relatórios Avançados'}
                </Title>
                {activeTabKey === 'transacoes' && (
                    <Space wrap style={{ width: isMobile ? '100%' : 'auto' }}>
                        <Button icon={<FilePdfOutlined />} onClick={exportPDF} block={isMobile}>
                            PDF
                        </Button>
                        <Button icon={<FileExcelOutlined />} onClick={exportExcel} block={isMobile}>
                            Excel
                        </Button>
                    </Space>
                )}
            </div>

            <Tabs
                activeKey={activeTabKey}
                onChange={handleTabChange}
                size={isMobile ? 'small' : 'middle'}
                type="card"
                className="sa-page-tabs"
            >
                <Tabs.TabPane
                    tab={isMobile ? <span><FilterOutlined /> Período</span> : <span><FilterOutlined /> Transações do período</span>}
                    key="transacoes"
                >
            {(vindoDoDesempenho || usarFiltrosDesempenho) && (
                <Alert
                    type="info"
                    showIcon
                    closable
                    onClose={() => setVindoDoDesempenho(false)}
                    style={{ marginBottom: 16 }}
                    message="Aberto a partir do Meu Desempenho"
                    description={
                        <>
                            Mesmo período e regra de data do gráfico
                            {usarFiltrosDesempenho ? ', sem os portadores e cartões que você ocultou lá' : ''}.
                            {' '}Recorrências ainda não lançadas entram no Desempenho como previsão, mas não aparecem nesta lista.
                            {usarFiltrosDesempenho && (
                                <div style={{ marginTop: 8 }}>
                                    <Button size="small" onClick={() => setUsarFiltrosDesempenho(false)}>
                                        Incluir portadores e cartões ocultos
                                    </Button>
                                </div>
                            )}
                        </>
                    }
                />
            )}
            <Card style={{ marginBottom: 24 }}>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={filtrosIniciais}
                    onValuesChange={handleValuesChange}
                    onFinish={handleSearch}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={6}>
                            <Form.Item name="periodo" label="Período" rules={[{ required: true }]}>
                                <RangePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    presets={[
                                        { label: 'Este Mês', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                                        { label: 'Mês Passado', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                                        { label: 'Últimos 30 Dias', value: [dayjs().subtract(30, 'days'), dayjs()] },
                                        { label: 'Este Ano', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
                                        { label: 'Tudo (Cuidado)', value: [dayjs('2020-01-01'), dayjs().endOf('year')] },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item
                                name="contas"
                                label={
                                    <Space>
                                        Contas
                                        <Tooltip title="Selecione Contas OU Cartões para evitar resultados vazios.">
                                            <InfoCircleOutlined style={{ color: '#E6A23C' }} />
                                        </Tooltip>
                                    </Space>
                                }
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Todas as contas"
                                    allowClear
                                    maxTagCount="responsive"
                                    disabled={disableContas}
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
                                    }
                                    optionLabelProp="label"
                                    options={contas.map(c => ({ key: c.id, value: c.id, label: c.nome }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item
                                name="cartoes"
                                label="Cartões de Crédito"
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Todos os cartões"
                                    allowClear
                                    maxTagCount="responsive"
                                    disabled={disableCartoes}
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
                                    }
                                    optionLabelProp="label"
                                    options={cartoes.map(c => ({ key: c.id, value: c.id, label: c.nome }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="search" label="Buscar Texto">
                                <Input placeholder="Descrição da transação..." prefix={<SearchOutlined />} allowClear />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={6}>
                            <Form.Item name="categorias" label="Categorias">
                                <Select
                                    mode="multiple"
                                    placeholder="Todas as categorias"
                                    allowClear
                                    maxTagCount="responsive"
                                    showSearch
                                    filterOption={(input, option) => {
                                        const label = option?.label ?? option?.children;
                                        return (label ?? '').toString().toLowerCase().includes(input.toLowerCase());
                                    }}
                                    optionLabelProp="label"
                                >
                                    <OptGroup label="Receitas">
                                        {categoriasReceita.map(c => <Option key={c.id} value={c.id} label={c.nome}>{c.nome}</Option>)}
                                    </OptGroup>
                                    <OptGroup label="Despesas">
                                        {categoriasDespesa.map(c => <Option key={c.id} value={c.id} label={c.nome}>{c.nome}</Option>)}
                                    </OptGroup>
                                </Select>
                            </Form.Item>
                        </Col>
                        {/* Removemos o filtro de Tipo pois agora as categorias já estão agrupadas,
                            mas mantemos opcionalmente se o usuário quiser filtrar macro */}
                        <Col xs={12} md={4}>
                            <Form.Item name="tipo" label="Tipo (Macro)">
                                <Select allowClear placeholder="Todos">
                                    <Option value="receita">Receitas</Option>
                                    <Option value="despesa">Despesas</Option>
                                    <Option value="transferencia">Transferências</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Item name="status" label="Status">
                                <Select allowClear placeholder="Todos">
                                    <Option value="PAGO">Pago / Recebido</Option>
                                    <Option value="PENDENTE">Pendente</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={4}>
                            <Form.Item
                                name="regime"
                                label={
                                    <Space>
                                        Contar pela data
                                        <Tooltip title="Data do lançamento: data de cada transação. Quando gastei / Quando a fatura vence: as mesmas regras do Meu Desempenho — no segundo, compras no cartão entram no mês em que a fatura vence.">
                                            <InfoCircleOutlined style={{ color: 'var(--color-text-secondary, #5A6A6A)' }} />
                                        </Tooltip>
                                    </Space>
                                }
                            >
                                <Select
                                    options={[
                                        { value: REGIME_LANCAMENTO, label: 'Do lançamento' },
                                        { value: 'competencia', label: 'Quando gastei' },
                                        { value: 'fatura', label: 'Quando a fatura vence' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <Form.Item style={{ width: '100%' }}>
                                <Button type="primary" htmlType="submit" icon={<FilterOutlined />} loading={loading} block>
                                    Filtrar Resultados
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {/* Hero do período + KPIs */}
            <div style={{ marginBottom: 24 }}>
                <Card className="desempenho-hero-card" bordered={false} style={{ marginBottom: 12 }}>
                    <div className="desempenho-hero-label">
                        <span>Resultado do período</span>
                    </div>
                    <div className={`desempenho-hero-value${totais.saldo < 0 ? ' is-negative' : ''}`}>
                        {Number(totais.saldo || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </div>
                    {saldosContas.contasCount > 0 && (
                        <div style={{ marginTop: 8, color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
                            Saldo atual em {saldosContas.contasCount} conta(s):{' '}
                            {Number(saldosContas.saldoAtual || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </div>
                    )}
                </Card>
                <Row gutter={[12, 12]}>
                    <Col xs={12} sm={8}>
                        <Card className="desempenho-kpi-card" bordered={false}>
                            <Statistic
                                title="Total receitas"
                                value={totais.receitas}
                                precision={2}
                                valueStyle={{ color: '#2F9E6F', fontVariantNumeric: 'tabular-nums', fontSize: isMobile ? 16 : undefined }}
                                prefix="R$"
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={8}>
                        <Card className="desempenho-kpi-card" bordered={false}>
                            <Statistic
                                title="Total despesas"
                                value={totais.despesas}
                                precision={2}
                                valueStyle={{ color: '#D64545', fontVariantNumeric: 'tabular-nums', fontSize: isMobile ? 16 : undefined }}
                                prefix="R$"
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card className="desempenho-kpi-card" bordered={false}>
                            <Statistic
                                title={
                                    <Space>
                                        Saldo atual (contas)
                                        <Tooltip title="Soma do saldo atual das contas do relatório. Já inclui saldo inicial e todo o movimento.">
                                            <InfoCircleOutlined style={{ color: 'var(--color-text-secondary, #5A6A6A)', fontSize: 14 }} />
                                        </Tooltip>
                                    </Space>
                                }
                                value={saldosContas.saldoAtual}
                                precision={2}
                                valueStyle={{ color: saldosContas.saldoAtual >= 0 ? '#2F9E6F' : '#D64545', fontVariantNumeric: 'tabular-nums', fontSize: isMobile ? 16 : undefined }}
                                prefix="R$"
                            />
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Insights on-brand */}
            {transacoes.length > 0 && (
                isMobile ? (
                    <Collapse
                        ghost
                        className="relatorios-insights-collapse"
                        style={{ marginBottom: 16 }}
                        items={[
                            {
                                key: 'insights',
                                label: <Text strong>Insights do período</Text>,
                                children: (
                                    <Row gutter={[12, 12]} className="insights-cards-row">
                                        <Col xs={12}>
                                            <Card size="small">
                                                <Statistic title="Maior despesa" value={insights.maiorDespesa ? parseFloat(insights.maiorDespesa.valor) : 0} precision={2} prefix="R$" valueStyle={{ fontSize: 16, color: '#D64545' }} />
                                            </Card>
                                        </Col>
                                        <Col xs={12}>
                                            <Card size="small">
                                                <Statistic title="Média/dia" value={insights.mediaPorDia} precision={2} prefix="R$" valueStyle={{ fontSize: 16 }} />
                                            </Card>
                                        </Col>
                                        <Col xs={24}>
                                            <Card size="small">
                                                <Statistic
                                                    title="Categoria top"
                                                    value={insights.categoriaTopGasto ? insights.categoriaTopGasto.valor : 0}
                                                    precision={2}
                                                    prefix="R$"
                                                    valueStyle={{ fontSize: 16 }}
                                                    suffix={insights.categoriaTopGasto ? <Text type="secondary" style={{ fontSize: 12 }}>{insights.categoriaTopGasto.nome}</Text> : null}
                                                />
                                            </Card>
                                        </Col>
                                    </Row>
                                ),
                            },
                        ]}
                    />
                ) : (
                <>
                    <Title level={4} style={{ marginBottom: 16, marginTop: 8, fontWeight: 600, letterSpacing: '-0.02em' }}>
                        Insights do período
                    </Title>
                    <Row gutter={[12, 12]} className="insights-cards-row" style={{ marginBottom: 24 }}>
                        <Col xs={24} sm={12} lg={6}>
                            <Card className="desempenho-kpi-card" bordered={false} style={{ background: 'rgba(184, 144, 64, 0.10)' }}>
                                <Statistic
                                    title={<Space><TrophyOutlined style={{ color: '#B89040' }} />Maior despesa</Space>}
                                    value={insights.maiorDespesa ? parseFloat(insights.maiorDespesa.valor) : 0}
                                    precision={2}
                                    valueStyle={{ color: '#D64545', fontSize: '20px' }}
                                    prefix="R$"
                                    suffix={insights.maiorDespesa ? <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>{insights.maiorDespesa.descricao}</Text> : null}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card className="desempenho-kpi-card" bordered={false} style={{ background: 'rgba(47, 158, 111, 0.10)' }}>
                                <Statistic
                                    title={<Space><RiseOutlined style={{ color: '#2F9E6F' }} />Top categoria</Space>}
                                    value={insights.categoriaTopGasto ? insights.categoriaTopGasto.valor : 0}
                                    precision={2}
                                    valueStyle={{ color: '#D64545', fontSize: '20px' }}
                                    prefix="R$"
                                    suffix={insights.categoriaTopGasto ? <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>{insights.categoriaTopGasto.nome}</Text> : null}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card className="desempenho-kpi-card" bordered={false} style={{ background: 'rgba(80, 176, 160, 0.12)' }}>
                                <Statistic
                                    title={<Space><CalculatorOutlined style={{ color: '#50B0A0' }} />Média/dia</Space>}
                                    value={insights.mediaPorDia}
                                    precision={2}
                                    valueStyle={{ color: '#50B0A0', fontSize: '20px' }}
                                    prefix="R$"
                                    suffix={<Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>em despesas</Text>}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card className="desempenho-kpi-card" bordered={false} style={{ background: 'rgba(214, 69, 69, 0.08)' }}>
                                <Statistic
                                    title={<Space><WarningOutlined style={{ color: '#D64545' }} />Grandes transações</Space>}
                                    value={insights.transacoesGrandes}
                                    valueStyle={{ color: '#D64545', fontSize: '20px' }}
                                    suffix={<Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>acima de R$ 500</Text>}
                                />
                                {insights.transacoesGrandes > 0 && (
                                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '8px' }}>
                                        Total: {insights.totalTransacoesGrandes.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </Text>
                                )}
                            </Card>
                        </Col>
                    </Row>
                </>
                )
            )}

            {isMobile ? (
                loading ? (
                    <Card loading />
                ) : dadosTabela.length === 0 ? (
                    <EmptyStateGuide
                        title="Nenhuma transação para os filtros selecionados."
                        description="Ajuste o período ou os filtros, ou registre lançamentos para gerar relatórios."
                        actionLabel="Ir para Transações"
                        onAction={() => navigate('/transacoes')}
                        icon={null}
                    />
                ) : (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {dadosTabela.map((record) => {
                            const contaLabel = record.conta_nome || record.cartao_nome || null;
                            return (
                                <Card
                                    key={record.id}
                                    size="small"
                                    className={`relatorio-tx-card-mobile${record.isSaldoInicial ? ' is-saldo-inicial' : ''}`}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {record.data_vencimento ? dayjs(record.data_vencimento).format('DD/MM/YYYY') : '—'}
                                            </Text>
                                            <div>
                                                <Text strong style={{ fontSize: 15 }}>
                                                    {record.descricao || '—'}
                                                </Text>
                                            </div>
                                            {contaLabel ? (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {contaLabel}
                                                    {record.categoria_nome && !record.isSaldoInicial ? ` · ${record.categoria_nome}` : ''}
                                                </Text>
                                            ) : null}
                                        </div>
                                        <Text
                                            strong
                                            type={record.isSaldoInicial ? undefined : (record.tipo === 'despesa' ? 'danger' : 'success')}
                                            style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                                        >
                                            {parseFloat(record.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                        </Text>
                                    </div>
                                    <div style={{ marginTop: 8 }}>
                                        {record.isSaldoInicial ? (
                                            <Tag>Saldo inicial</Tag>
                                        ) : (
                                            <Space wrap size={[4, 4]}>
                                                <Tag color={record.status === 'PAGO' ? 'green' : record.status === 'PENDENTE' ? 'orange' : 'default'}>
                                                    {record.status}
                                                </Tag>
                                                {record.is_transferencia ? <Tag color="blue">Transferência</Tag> : null}
                                            </Space>
                                        )}
                                    </div>
                                </Card>
                            );
                        })}
                    </Space>
                )
            ) : (
            <Table
                columns={columns}
                dataSource={dadosTabela}
                rowKey="id"
                loading={loading}
                rowClassName={(record) => record.isSaldoInicial ? 'row-saldo-inicial' : ''}
                pagination={{ 
                    pageSize: pageSize, 
                    showSizeChanger: true,
                    onShowSizeChange: (current, size) => setPageSize(size),
                    pageSizeOptions: ['10', '20', '50', '100', '200']
                }}
                locale={{
                    emptyText: (
                        <EmptyStateGuide
                            title="Nenhuma transação para os filtros selecionados."
                            description="Ajuste o período ou os filtros, ou registre lançamentos para gerar relatórios."
                            actionLabel="Ir para Transações"
                            onAction={() => navigate('/transacoes')}
                            icon={null}
                        />
                    ),
                }}
            />
            )}
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={isMobile ? <span><UnorderedListOutlined /> Parcelamentos</span> : <span><UnorderedListOutlined /> Parcelamentos</span>}
                    key="parcelamentos"
                >
                    <ParcelamentosFuturosChart isMobile={isMobile} />

                    <Card style={{ marginBottom: 16 }}>
                        <Form layout="vertical" style={{ marginBottom: 0 }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} md={12} lg={8}>
                                    <Form.Item label="Buscar por nome" style={{ marginBottom: 0 }}>
                                        <Input
                                            prefix={<SearchOutlined />}
                                            placeholder="Buscar por nome/descrição"
                                            allowClear
                                            value={filtroParcelamentosDescricao}
                                            onChange={(e) => setFiltroParcelamentosDescricao(e.target.value)}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={6} lg={4}>
                                    <Form.Item label="Tipo" style={{ marginBottom: 0 }}>
                                        <Select placeholder="Todos" allowClear value={filtroParcelamentosTipo} onChange={setFiltroParcelamentosTipo} style={{ width: '100%' }}>
                                            <Option value="despesa">Despesa</Option>
                                            <Option value="receita">Receita</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={6} lg={4}>
                                    <Form.Item label="Origem" style={{ marginBottom: 0 }}>
                                        <Select
                                            placeholder="Todos"
                                            allowClear
                                            value={filtroParcelamentosOrigem}
                                            onChange={(valor) => {
                                                setFiltroParcelamentosOrigem(valor);
                                                // Cartão + origem "Conta" nunca casam: limpa para não cair
                                                // numa lista vazia sem explicação.
                                                if (valor === 'lancamento_conta') setFiltroParcelamentosCartao(null);
                                            }}
                                            style={{ width: '100%' }}
                                        >
                                            <Option value="lancamento_conta">Conta</Option>
                                            <Option value="compra_cartao">Cartão</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={6} lg={4}>
                                    <Form.Item label="Cartão" style={{ marginBottom: 0 }}>
                                        <Select
                                            placeholder="Todos"
                                            allowClear
                                            showSearch
                                            optionFilterProp="label"
                                            disabled={filtroParcelamentosOrigem === 'lancamento_conta'}
                                            value={filtroParcelamentosCartao}
                                            onChange={setFiltroParcelamentosCartao}
                                            style={{ width: '100%' }}
                                            options={cartoes.map(c => ({ value: c.id, label: c.nome }))}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={6} lg={4}>
                                    <Form.Item label="Situação" style={{ marginBottom: 0 }}>
                                        <Select placeholder="Todos" allowClear value={filtroParcelamentosSituacao} onChange={setFiltroParcelamentosSituacao} style={{ width: '100%' }}>
                                            <Option value="aberto">Em aberto</Option>
                                            <Option value="quitado">Quitados</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </Card>

                    {(loadingParcelamentos || parcelamentosFiltrados.length > 0) && (
                        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                            <Col xs={12} sm={12} md={6}>
                                <Card size="small" loading={loadingParcelamentos}>
                                    <Statistic
                                        title="Comprometido (futuro)"
                                        value={totaisParcelamentos.comprometido}
                                        precision={2}
                                        prefix="R$"
                                        valueStyle={{ color: '#D64545', fontSize: 18 }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                                <Card size="small" loading={loadingParcelamentos}>
                                    <Statistic
                                        title="Já quitado"
                                        value={totaisParcelamentos.quitado}
                                        precision={2}
                                        prefix="R$"
                                        valueStyle={{ color: '#2F9E6F', fontSize: 18 }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                                <Card size="small" loading={loadingParcelamentos}>
                                    <Statistic
                                        title="Parcelas em aberto"
                                        value={totaisParcelamentos.parcelasEmAberto}
                                        valueStyle={{ fontSize: 18 }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                                <Card size="small" loading={loadingParcelamentos}>
                                    <Statistic
                                        title="Receitas a receber"
                                        value={totaisParcelamentos.receitasAReceber}
                                        precision={2}
                                        prefix="R$"
                                        valueStyle={{ color: '#2F9E6F', fontSize: 18 }}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {isMobile ? (
                        <>
                            {loadingParcelamentos ? (
                                <Card loading />
                            ) : parcelamentosFiltrados.length === 0 ? (
                                <EmptyStateGuide
                                    title="Nenhum parcelamento encontrado."
                                    description="Compras parceladas em contas ou cartões aparecerão aqui."
                                />
                            ) : (
                                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                    {parcelamentosFiltrados.map((p) => {
                                        const valorRestante = parseFloat(p.valor_restante || 0);
                                        const valorPago = Math.max(0, parseFloat(p.valor_total || 0) - valorRestante);
                                        const totalParc = parseInt(p.total_parcelas || 0, 10) || 1;
                                        const pagas = parseInt(p.parcelas_pagas || 0, 10);
                                        const percent = Math.min(100, Math.round((pagas / totalParc) * 100));
                                        return (
                                            <Card key={`${p.origem}-${p.id}`} size="small" className="parcelamento-card-mobile">
                                                <div style={{ marginBottom: 8 }}>
                                                    <Text strong>{p.descricao}</Text>
                                                    {(p.cartao_nome || p.conta_nome) && (
                                                        <div><Text type="secondary" style={{ fontSize: 12 }}>{p.cartao_nome || p.conta_nome}</Text></div>
                                                    )}
                                                </div>
                                                <Progress percent={percent} size="small" status={percent >= 100 ? 'success' : 'active'} />
                                                <Text type="secondary" style={{ fontSize: 12 }}>{pagas}/{totalParc} parcelas</Text>
                                                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                                                    <div>
                                                        <Text type="secondary">Quitado: </Text>
                                                        <Text type="success">{fmtBRL(valorPago)}</Text>
                                                    </div>
                                                    <div>
                                                        <Text type="secondary">Restante: </Text>
                                                        {valorRestante === 0
                                                            ? '—'
                                                            : (
                                                                <Text type={p.tipo === 'receita' ? 'success' : 'danger'} strong>
                                                                    {fmtBRL(valorRestante)}
                                                                </Text>
                                                            )}
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: 8 }}>
                                                    {p.origem === 'compra_cartao' ? <Tag color="green">Cartão</Tag> : <Tag color="blue">Conta</Tag>}
                                                    {p.tipo === 'receita' ? <Tag color="green">Receita</Tag> : <Tag color="red">Despesa</Tag>}
                                                </div>
                                            </Card>
                                        );
                                    })}
                                </Space>
                            )}
                            {parcelamentosFiltrados.length > 0 && (
                                <Card size="small" style={{ marginTop: 16 }}>
                                    <Row gutter={[8, 8]}>
                                        <Col span={24}>
                                            <Text type="secondary">Comprometido (futuro): </Text>
                                            <Text type="danger" strong>{fmtBRL(totaisParcelamentos.comprometido)}</Text>
                                        </Col>
                                        <Col span={24}>
                                            <Text type="secondary">Já quitado: </Text>
                                            <Text type="success" strong>{fmtBRL(totaisParcelamentos.quitado)}</Text>
                                        </Col>
                                        <Col span={24}>
                                            <Text type="secondary">Parcelas em aberto: </Text>
                                            <Text strong>{totaisParcelamentos.parcelasEmAberto}</Text>
                                        </Col>
                                    </Row>
                                </Card>
                            )}
                        </>
                    ) : (
                        <Table
                            className="table-parcelamentos"
                            columns={columnsParcelamentos}
                            dataSource={parcelamentosFiltrados}
                            rowKey={(r) => `${r.origem}-${r.id}`}
                            loading={loadingParcelamentos}
                            pagination={{
                                pageSize: pageSizeParcelamentos,
                                showSizeChanger: true,
                                onShowSizeChange: (_c, size) => setPageSizeParcelamentos(size),
                                onChange: (_page, size) => {
                                    if (size && size !== pageSizeParcelamentos) setPageSizeParcelamentos(size);
                                },
                                pageSizeOptions: ['10', '20', '50', '100'],
                                showTotal: (total) => `${total} parcelamento(s)`,
                            }}
                            locale={{
                                emptyText: (
                                    <EmptyStateGuide
                                        title="Nenhum parcelamento encontrado."
                                        description="Compras parceladas em contas ou cartões aparecerão aqui."
                                    />
                                ),
                            }}
                            scroll={{ x: 1100 }}
                            summary={() => (
                                <Table.Summary>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={5}>
                                            <Text strong>Comprometido (despesas futuras)</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} align="right" colSpan={2}>
                                            <Text type="danger" strong>{fmtBRL(totaisParcelamentos.comprometido)}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2} colSpan={2} />
                                    </Table.Summary.Row>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={5}>
                                            <Text strong>Já quitado</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} align="right" colSpan={2}>
                                            <Text type="success" strong>{fmtBRL(totaisParcelamentos.quitado)}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2} colSpan={2} />
                                    </Table.Summary.Row>
                                </Table.Summary>
                            )}
                        />
                    )}
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={<span><CreditCardOutlined /> Cartões</span>}
                    key="cartoes"
                >
                    <UsoPorDiaChart isMobile={isMobile} />
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={<span><TeamOutlined /> Por portador</span>}
                    key="portadores"
                >
                    <RelatoriosPortadorTab cartoes={cartoes} isMobile={isMobile} />
                </Tabs.TabPane>
            </Tabs>
        </Content>
    );
};

export default RelatoriosPage;
